import {
  formatActivityDateTime,
  getWeightDisplayValue,
} from "../utils/collectionRowUtils";
import {
  CompactionIcon,
  FillingLevelIcon,
  ImportedDocumentIcon,
  NotesIcon,
  WeightTonsIcon,
} from "../../../ui/components/MonitoringMetricIcons";

const AKANTHAS_CYAN = "#00A7A6";
const SECTION_BORDER = "border-t-[1.2px] border-[#D3D3D3]";

const InlineInfoLine = ({ label, value, dimmed = false }) => (
  <p
    className={`text-sm text-gray-500 font-normal m-0 ${dimmed ? "opacity-50" : ""}`}
  >
    {label}: {value}
  </p>
);

const MetricRow = ({
  label,
  value,
  icon = null,
  dimmed = false,
  valueBold = true,
}) => {
  const hasValue = value !== "" && value != null;

  return (
    <div
      className={`flex items-center justify-between gap-3 ${dimmed ? "opacity-50" : ""}`}
    >
      <div className="flex items-center gap-2 min-w-0">
        {icon}
        <span className="text-sm text-gray-700">{label}</span>
      </div>
      {hasValue ? (
        <span
          className={`text-sm shrink-0 ${valueBold ? "font-bold text-gray-900" : "font-normal text-gray-500"}`}
        >
          {value}
        </span>
      ) : null}
    </div>
  );
};

const ActionCountRow = ({ label, count, icon, onClick, disabled = false }) => (
  <div
    className={`flex items-center justify-between gap-3 ${disabled ? "opacity-50" : ""}`}
  >
    <div className="flex items-center gap-2 min-w-0">
      {icon}
      <span className="text-sm text-gray-700">{label}</span>
    </div>
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onClick?.(event);
      }}
      disabled={disabled}
      className="text-sm font-bold shrink-0 border-0 bg-transparent p-0 cursor-pointer underline underline-offset-[3px] decoration-1 hover:opacity-80 disabled:cursor-not-allowed disabled:no-underline"
      style={{ color: AKANTHAS_CYAN, textDecorationColor: AKANTHAS_CYAN }}
    >
      {count}
    </button>
  </div>
);

const CollectionRowInfo = ({
  timeLine,
  historyCycle,
  collectionFillingLevel,
  numberOfPackmat,
  clientRegisteredWeight,
  toCharacterization = false,
  weightToTonnes,
  toFrenchNumber,
  loading,
  files = [],
  notesCount = 0,
  onOpenFiles,
  onOpenNotes,
  loadScreen = false,
  t,
}) => {
  const shouldHideEstimatedWeight =
    toCharacterization &&
    (timeLine?.characterization === true || timeLine?.qualicheck === true) &&
    !clientRegisteredWeight &&
    !(historyCycle?.length === 1 || timeLine?.density === 0);

  const isRowDisabled =
    timeLine?.fictiveContainer?.active || loadScreen || loading;

  const dropOffDate =
    historyCycle?.length > 0
      ? formatActivityDateTime(historyCycle[historyCycle.length - 1]?._time, t)
      : "...";

  const pickUpVisible =
    historyCycle?.length > 1 &&
    (timeLine.showDepotCycle === undefined || !timeLine.showDepotCycle);

  const pickUpDate = pickUpVisible
    ? formatActivityDateTime(timeLine._time, t)
    : null;

  const weightLabel = clientRegisteredWeight
    ? t("Registered weight")
    : t("Estimated weight");

  const weightValue =
    historyCycle && historyCycle.length > 0 && !loading
      ? getWeightDisplayValue(
          clientRegisteredWeight,
          timeLine,
          historyCycle,
          weightToTonnes,
          toFrenchNumber,
          t,
        )
      : "...";

  const showCollectionMetrics =
    (timeLine.activatedFillingLevelOption &&
      (timeLine.showDepotCycle === undefined || !timeLine.showDepotCycle)) ||
    !shouldHideEstimatedWeight ||
    timeLine.packmatActive;

  return (
    <div className="flex flex-col w-full min-w-0 pt-3 mt-2">
      <div className="flex flex-col gap-1">
        {historyCycle && (
          <InlineInfoLine label={t("Drop-off")} value={dropOffDate} />
        )}
        {pickUpDate && (
          <InlineInfoLine label={t("Pick-up")} value={pickUpDate} />
        )}
      </div>

      {showCollectionMetrics && (
        <div className={`flex flex-col gap-3 pt-3 mt-3 ${SECTION_BORDER}`}>
          {timeLine.activatedFillingLevelOption &&
            (timeLine.showDepotCycle === undefined ||
              !timeLine.showDepotCycle) && (
              <MetricRow
                label={t("Level at collection")}
                value={`${collectionFillingLevel}%`}
                dimmed={collectionFillingLevel === undefined}
                icon={<FillingLevelIcon />}
              />
            )}

          {!shouldHideEstimatedWeight && (
            <MetricRow
              label={weightLabel}
              value={weightValue}
              dimmed={loading || !historyCycle?.length}
              icon={<WeightTonsIcon />}
            />
          )}

          {timeLine.packmatActive && (
            <MetricRow
              label={t("Compaction")}
              value={numberOfPackmat ?? "..."}
              dimmed={numberOfPackmat === undefined}
              icon={<CompactionIcon />}
            />
          )}
        </div>
      )}

      <div className={`flex flex-col gap-3 pt-3 mt-3 ${SECTION_BORDER}`}>
        <ActionCountRow
          label={t("Imported documents")}
          count={files?.length ?? 0}
          icon={<ImportedDocumentIcon />}
          onClick={onOpenFiles}
          disabled={isRowDisabled}
        />

        <ActionCountRow
          label={t("Comments")}
          count={notesCount}
          icon={<NotesIcon />}
          onClick={onOpenNotes}
          disabled={isRowDisabled}
        />
      </div>
    </div>
  );
};

export default CollectionRowInfo;
